// ============================================================
// LoginPage.js — Page de connexion personnalisée
// Deux modes :
//   - Bouton Google  → redirige via Auth0 (PKCE, géré par loginApp)
//   - Formulaire     → email + mot de passe (ROPG)
// Props :
//   onLogin(email, password)   — connexion email/mdp
//   onGoogleLogin()            — connexion Google
//   processingCallback         — true pendant l'échange du token Google
//   callbackError              — message d'erreur OAuth si échec
// ============================================================

import React, { useState } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

// Icône Google (couleurs officielles)
const GoogleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20" style={{ marginRight: '10px', flexShrink: 0 }}>
        <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
);

// Barre de navigation partagée avec l'app principale
const AppBar = () => (
    <div style={{ backgroundColor: '#1a5c38', padding: '12px 24px', display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '0.05em' }}>AVALIST</span>
    </div>
);

const LoginPage = ({ onLogin, onGoogleLogin, processingCallback, callbackError, onForgotPassword }) => {
    const [email,       setEmail]       = useState('');
    const [password,    setPassword]    = useState('');
    const [showPwd,     setShowPwd]     = useState(false);
    const [error,       setError]       = useState('');
    const [loading,     setLoading]     = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await onLogin(email, password);
        } catch (err) {
            setError(err.message || 'Identifiants incorrects. Vérifiez votre e-mail et mot de passe.');
            setLoading(false);
        }
    };

    // Écran intermédiaire pendant le retour de Google
    if (processingCallback) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
                <AppBar />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        <Spinner animation="border" style={{ color: '#2d8a4e', width: '48px', height: '48px', marginBottom: '16px' }} />
                        <p style={{ fontWeight: 500, fontSize: '1rem', color: '#333' }}>Connexion Google en cours…</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
            <AppBar />

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 16px' }}>
                <div style={{
                    backgroundColor : 'white',
                    borderRadius    : '12px',
                    boxShadow       : '0 4px 24px rgba(0,0,0,0.10)',
                    padding         : '40px 36px',
                    width           : '100%',
                    maxWidth        : '400px',
                }}>
                    <h2 style={{ color: '#1a5c38', fontWeight: 700, marginBottom: '6px', fontSize: '1.5rem' }}>
                        Connexion
                    </h2>
                    <p style={{ color: '#666', marginBottom: '28px', fontSize: '0.92rem' }}>
                        Accédez à la gestion des parcs
                    </p>

                    {(error || callbackError) && (
                        <Alert variant="danger" style={{ borderRadius: '8px', fontSize: '0.9rem' }}>
                            {error || callbackError}
                        </Alert>
                    )}

                    {/* Bouton Google */}
                    <Button
                        onClick={onGoogleLogin}
                        style={{
                            width           : '100%',
                            backgroundColor : 'white',
                            border          : '1px solid #d0d0d0',
                            color           : '#333',
                            padding         : '10px',
                            borderRadius    : '8px',
                            fontWeight      : 500,
                            fontSize        : '0.95rem',
                            display         : 'flex',
                            alignItems      : 'center',
                            justifyContent  : 'center',
                            marginBottom    : '20px',
                            boxShadow       : '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                    >
                        <GoogleIcon /> Continuer avec Google
                    </Button>

                    {/* Séparateur */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                        <span style={{ color: '#999', fontSize: '0.82rem' }}>ou</span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                    </div>

                    {/* Formulaire email + mot de passe */}
                    <Form onSubmit={handleSubmit}>
                        <Form.Group style={{ marginBottom: '16px' }}>
                            <Form.Label style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
                                Adresse e-mail
                            </Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="prenom.nom@exemple.fr"
                                required
                                autoFocus
                                style={{ borderRadius: '8px', border: '1px solid #d0d0d0', padding: '10px 14px', fontSize: '0.95rem' }}
                            />
                        </Form.Group>

                        <Form.Group style={{ marginBottom: '16px' }}>
                            <Form.Label style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
                                Mot de passe
                            </Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showPwd ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    style={{ borderRadius: '8px', border: '1px solid #d0d0d0', padding: '10px 42px 10px 14px', fontSize: '0.95rem' }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(!showPwd)}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#888', fontSize: '1.1rem', lineHeight: 1 }}
                                    tabIndex="-1"
                                >
                                    {showPwd ? '🙈' : '👁️'}
                                </button>
                            </div>
                            <div style={{ textAlign: 'right', marginTop: '6px' }}>
                                <button
                                    type="button"
                                    onClick={onForgotPassword}
                                    style={{ background: 'none', border: 'none', color: '#2d8a4e', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}
                                >
                                    Mot de passe oublié ?
                                </button>
                            </div>
                        </Form.Group>

                        <Button
                            type="submit"
                            disabled={loading}
                            style={{
                                width           : '100%',
                                backgroundColor : '#2d8a4e',
                                borderColor     : '#2d8a4e',
                                color           : 'white',
                                padding         : '11px',
                                borderRadius    : '8px',
                                fontWeight      : 600,
                                fontSize        : '1rem',
                            }}
                        >
                            {loading
                                ? <><Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />Connexion en cours…</>
                                : 'Se connecter'
                            }
                        </Button>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
